import { Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import React from 'react';
import { MinPercentageDialog } from '../components/common/dialogs/MinPercentageDialog';
import styles from '../styles/components/Dialogs.module.scss';

/**
 * Унифицированные диалоги подтверждения.
 * 
 * Все методы возвращают Promise<boolean>, который резолвится:
 * - true - пользователь подтвердил действие
 * - false - пользователь отменил действие
 * 
 * @example
 * ```typescript
 * import { confirmDialog } from '@/utils/confirmDialogs';
 * 
 * // Подтверждение сохранения
 * const shouldSave = await confirmDialog.save(5);
 * if (shouldSave) {
 *   await saveChanges();
 * }
 * 
 * // Подтверждение перезагрузки с потерей данных
 * const shouldReload = await confirmDialog.reloadWithLoss(3);
 * 
 * // Подтверждение удаления
 * const shouldDelete = await confirmDialog.delete('Пользователь');
 * ```
 */
export const confirmDialog = {
  /**
   * Диалог подтверждения сохранения изменений
   * @param count - Количество изменений для сохранения
   * @returns Promise<boolean> - true если пользователь подтвердил
   */
  save: (count: number): Promise<boolean> => {
    return new Promise((resolve) => {
      Modal.confirm({
        title: 'Сохранить изменения?',
        content: `Будет сохранено ${count} ${pluralize(count, 'изменение', 'изменения', 'изменений')}. Продолжить?`,
        okText: 'Сохранить',
        cancelText: 'Отмена',
        okType: 'primary',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
  },

  /**
   * Диалог подтверждения перезагрузки с потерей несохраненных изменений
   * @param dirtyCount - Количество несохраненных изменений
   * @returns Promise<boolean> - true если пользователь подтвердил
   */
  reloadWithLoss: (dirtyCount: number): Promise<boolean> => {
    return new Promise((resolve) => {
      Modal.confirm({
        title: 'Несохраненные изменения',
        content: `У вас есть ${dirtyCount} ${pluralize(dirtyCount, 'несохраненное изменение', 'несохраненных изменения', 'несохраненных изменений')}. При обновлении они будут потеряны. Продолжить?`,
        okText: 'Обновить',
        cancelText: 'Отмена',
        okType: 'danger',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
  },

  /**
   * Диалог подтверждения удаления элемента
   * @param itemName - Название удаляемого элемента
   * @returns Promise<boolean> - true если пользователь подтвердил
   */
  delete: (itemName: string): Promise<boolean> => {
    return new Promise((resolve) => {
      Modal.confirm({
        title: 'Удалить элемент?',
        content: `Вы уверены, что хотите удалить "${itemName}"? Это действие нельзя отменить.`,
        okText: 'Удалить',
        cancelText: 'Отмена',
        okType: 'danger',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
  },

  /**
   * Диалог подтверждения деактивации элемента
   * @param itemName - Название деактивируемого элемента
   * @returns Promise<boolean> - true если пользователь подтвердил
   */
  deactivate: (itemName: string): Promise<boolean> => {
    return new Promise((resolve) => {
      Modal.confirm({
        title: 'Деактивировать элемент?',
        content: `Вы уверены, что хотите деактивировать "${itemName}"?`,
        okText: 'Деактивировать',
        cancelText: 'Отмена',
        okType: 'danger',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
  },

  /**
   * Универсальный диалог подтверждения с кастомными параметрами
   * @param options - Параметры диалога
   * @returns Promise<boolean> - true если пользователь подтвердил
   */
  custom: (options: {
    title: string;
    content: string;
    okText?: string;
    cancelText?: string;
    okType?: 'primary' | 'danger';
  }): Promise<boolean> => {
    return new Promise((resolve) => {
      Modal.confirm({
        title: options.title,
        content: options.content,
        okText: options.okText || 'OK',
        cancelText: options.cancelText || 'Отмена',
        okType: options.okType || 'primary',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
  },

  /**
   * Элегантный диалог для установки минимального процента
   * @param options - Параметры диалога
   * @returns Promise<boolean> - true если пользователь подтвердил
   */
  minPercentage: (options: {
    type: 'amount' | 'percentage';
    entityType: 'revenue' | 'knr';
    inputValue: number;
    calculatedPercent: number;
    minAmount: number;
  }): Promise<boolean> => {
    return new Promise((resolve) => {
      Modal.confirm({
        icon: React.createElement(ExclamationCircleOutlined, { 
          style: { color: '#faad14', fontSize: '22px' } 
        }),
        title: <div className={styles.minPercentageTitle}>Слишком малое значение</div>,
        content: <MinPercentageDialog {...options} />,
        okText: <span style={{ fontSize: '14px', fontWeight: 500 }}>Установить</span>,
        cancelText: 'Отменить',
        okType: 'primary',
        width: 480,
        centered: true,
        maskClosable: false,
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
  },
};

/**
 * Вспомогательная функция для склонения слов
 * @param count - Число
 * @param one - Форма для 1 (изменение)
 * @param few - Форма для 2-4 (изменения)
 * @param many - Форма для 5+ (изменений)
 */
function pluralize(count: number, one: string, few: string, many: string): string {
  const absCount = Math.abs(count);
  const lastTwo = absCount % 100;
  const lastOne = absCount % 10;

  if (lastTwo >= 11 && lastTwo <= 19) {
    return many;
  }

  if (lastOne === 1) {
    return one;
  }

  if (lastOne >= 2 && lastOne <= 4) {
    return few;
  }

  return many;
}
